import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ChatTeardropDots, Briefcase, Buildings, Plus } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { PageHeader } from '@/components/shared/PageHeader';
import { MessagingView } from '@/components/messaging/MessagingView';
import { fetchThreads, type ThreadKind } from '@/lib/messagingApi';
import { useAuthStore } from '@/features/auth/store';
import { QueryErrorState } from '@/components/shared/QueryErrorState';
import { cn } from '@/lib/utils';
import { useNavigate } from 'react-router-dom';
import { useFillViewportHeight } from '@/hooks/useFillViewportHeight';

type KindFilter = ThreadKind | null;

const KIND_CHIPS: { key: KindFilter; label: string; Icon: React.ElementType }[] = [
  { key: null,                label: 'All',      Icon: ChatTeardropDots },
  { key: 'matter_internal',   label: 'Matters',  Icon: Briefcase        },
  { key: 'chambers_internal', label: 'Internal', Icon: Buildings        },
];

export default function CommunicationsPage() {
  const navigate = useNavigate();
  const currentUser = useAuthStore((s) => s.user);
  const [kindFilter, setKindFilter] = useState<KindFilter>(null);
  const [paneRef, paneHeight] = useFillViewportHeight();

  const { data: allThreads = [], isError } = useQuery({
    queryKey: ['messaging', 'threads', 'stats'],
    queryFn: () => fetchThreads({ limit: 100 }),
    enabled: !!currentUser,
    staleTime: 30_000,
    refetchInterval: 120_000,
  });

  if (isError) {
    return <QueryErrorState what="communications" />;
  }

  const totalUnread    = allThreads.reduce((s, t) => s + (t.unreadCount ?? 0), 0);
  const matterUnread   = allThreads.filter(t => t.kind === 'matter_internal'  && (t.unreadCount ?? 0) > 0).length;
  const internalUnread = allThreads.filter(t => t.kind === 'chambers_internal' && (t.unreadCount ?? 0) > 0).length;

  return (
    <div className="flex flex-col gap-2">
      <PageHeader
        onBack={() => navigate(-1)}
        title="Communications"
        subtitle="Matter and chambers message threads, most recent first."
        className="mb-2"
      />
      {/* The strip's own "Communications" label is gone: with a real page title above it, it
          said the same word twice. The leading divider went with it. */}
      {/* The strip is four groups — counts, divider, kind chips, "New matter" — and it could not
          wrap, so on a phone it ran off the right edge: "Internal" and the button were outside
          the screen and the "Matters" chip was sliced in half. It now wraps onto as many lines
          as it needs. One line, unchanged, from the moment the row fits. */}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-lg border border-border bg-card px-4 py-2.5">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
          <span className={cn('flex items-center gap-1', totalUnread > 0 ? 'font-bold text-brand-700 dark:text-brand-300' : 'text-muted-foreground')}>
            <ChatTeardropDots className="h-3 w-3" />{totalUnread} unread
          </span>
          <span className={cn('flex items-center gap-1', matterUnread > 0 ? 'font-semibold text-brand-600 dark:text-brand-400' : 'text-muted-foreground')}>
            <Briefcase className="h-3 w-3" />{matterUnread} matters
          </span>
          <span className={cn('flex items-center gap-1', internalUnread > 0 ? 'font-semibold text-brand-600 dark:text-brand-400' : 'text-muted-foreground')}>
            <Buildings className="h-3 w-3" />{internalUnread} internal
          </span>
        </div>

        <div className="mx-1 hidden h-4 w-px bg-border sm:block" />

        <div className="flex flex-wrap items-center gap-1">
          {KIND_CHIPS.map(({ key, label, Icon }) => (
            <button
              key={String(key)}
              type="button"
              onClick={() => setKindFilter(key)}
              className={cn(
                'inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-full border px-2.5 py-0.5 text-xs font-medium transition',
                kindFilter === key
                  ? 'border-brand-600 bg-brand-600 text-white'
                  : 'border-border bg-muted/40 text-muted-foreground hover:border-brand-300 hover:text-foreground',
              )}
            >
              <Icon className="h-3 w-3" />{label}
            </button>
          ))}
        </div>

        <div className="ml-auto shrink-0">
          <Button variant="outline" size="sm" className="whitespace-nowrap" leftIcon={<Plus className="h-3.5 w-3.5" />} onClick={() => navigate('/admin/matters')}>
            New matter
          </Button>
        </div>
      </div>

      {/* The panel used to subtract a hand-counted 148px from the viewport. That number was
          counted against a desktop header on one line; on a phone the title, its subtitle and
          the filter strip above come to nearly twice it, so the bottom of the conversation list
          sat below the fold. It now measures where it actually starts. */}
      <div ref={paneRef}>
        <MessagingView kindFilter={kindFilter} height={paneHeight} />
      </div>
    </div>
  );
}
